import React from 'react'
import { Input, Modal, Typography } from 'antd'
import { AxiosError } from 'axios'
import { authApi } from 'api/auth'
import {
  getContactCodeCooldownSeconds,
  setContactCodeCooldown
} from 'shared/utils/contactCodeCooldown'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import styles from './PhoneVerificationModal.module.scss'

const CODE_LENGTH = 4

interface IPhoneVerificationModalProps {
  isOpen: boolean
  phone: number
  onClose: () => void
  onVerified?: (phone: number) => void
  // Без них код запрашивается и проверяется для того, кто авторизован
  requestCode?: (phone: number) => Promise<unknown>
  confirmCode?: (code: number) => Promise<unknown>
  // Пауза между отправками по умолчанию считается по номеру
  cooldownKey?: string | number
}

const requestOwnCode = (phone: number) => authApi.sendPhoneCode({ phone })
const confirmOwnCode = (code: number) => authApi.verifyPhone({ code })

export const PhoneVerificationModal: React.FC<IPhoneVerificationModalProps> = ({
  isOpen,
  phone,
  onClose,
  onVerified,
  requestCode = requestOwnCode,
  confirmCode = confirmOwnCode,
  cooldownKey = phone
}) => {
  const [isVerifying, setIsVerifying] = React.useState(false)
  const [error, setError] = React.useState('')
  const [timer, setTimer] = React.useState(0)
  // ремонтируем поля кода, чтобы очистить их после неверной попытки
  const [codeFieldKey, setCodeFieldKey] = React.useState(0)

  React.useEffect(() => {
    if (!isOpen) return

    setError('')
    setCodeFieldKey((key) => key + 1)
    setTimer(getContactCodeCooldownSeconds(cooldownKey))

    const interval = setInterval(
      () => setTimer(getContactCodeCooldownSeconds(cooldownKey)),
      1000
    )
    return () => clearInterval(interval)
  }, [isOpen, cooldownKey])

  const verifyCode = async (code: string) => {
    try {
      setIsVerifying(true)
      setError('')
      await confirmCode(Number(code))
      showMessage('success', 'Телефон подтверждён', '')
      onVerified?.(phone)
      onClose()
    } catch (e) {
      const message = (e as AxiosError<{ message: string }>).response?.data
        ?.message
      setError(message ?? 'Неверный код подтверждения')
      setCodeFieldKey((key) => key + 1)
    } finally {
      setIsVerifying(false)
    }
  }

  const resendCode = async () => {
    try {
      await requestCode(phone)
      setContactCodeCooldown(cooldownKey)
      setTimer(getContactCodeCooldownSeconds(cooldownKey))
      setError('')
      setCodeFieldKey((key) => key + 1)
      showMessage('success', 'Код отправлен повторно', '')
    } catch (e) {
      const message = (e as AxiosError<{ message: string }>).response?.data
        ?.message
      // код уже отправлен, а вкладка об этом не знает — заводим таймер по ответу
      if (message?.includes('секунд')) {
        setContactCodeCooldown(cooldownKey)
        setTimer(getContactCodeCooldownSeconds(cooldownKey))
      }
      showServerError(e)
    }
  }

  return (
    <Modal
      open={isOpen}
      title='Подтверждение телефона'
      footer={null}
      onCancel={onClose}
    >
      <div className={styles.wrapper}>
        <Typography.Text>
          Код подтверждения отправлен по SMS на номер {phoneMaskStatic(phone)}.
          Код действует 10 минут.
        </Typography.Text>
        <Input.OTP
          key={codeFieldKey}
          length={CODE_LENGTH}
          disabled={isVerifying}
          status={error ? 'error' : undefined}
          formatter={(value) => value.replace(/\D/g, '')}
          onChange={verifyCode}
        />
        {error && <Typography.Text type='danger'>{error}</Typography.Text>}
        <Typography.Link
          disabled={timer > 0 || isVerifying}
          onClick={resendCode}
        >
          {timer > 0
            ? `Отправить код повторно можно через ${timer} сек`
            : 'Отправить код повторно'}
        </Typography.Link>
      </div>
    </Modal>
  )
}
